/**
 * Client half of the console live-update contract (#544 over the server contract of #549).
 *
 * The server names the domains whose data moved; this module turns that into one counter per
 * domain. A screen depends on the counters of the domains it renders and refetches when one moves,
 * so invalidation lives in a single place instead of in every screen's own polling.
 *
 * Everything here is pure so it can be tested without a DOM or a network: the provider owns the
 * EventSource, this module owns the rules.
 */

export const CONSOLE_DOMAINS = [
  "requests",
  "workspace",
  "customers",
  "staff",
  "settings",
  "tenants",
  "catalog",
  "pipeline",
  "mailbox",
] as const;

export type ConsoleDomain = (typeof CONSOLE_DOMAINS)[number];

export type ConsoleEventScope = "tenant" | "owner_global";

export interface ConsoleChangedEvent {
  type: "console.changed";
  contract_version?: number;
  scope?: ConsoleEventScope;
  scope_id?: string | null;
  seq?: number;
  version: string;
  changed_at: string;
  domains?: ConsoleDomain[];
  domain_versions?: Partial<Record<ConsoleDomain, string>>;
}

export interface ConsoleHeartbeatEvent {
  type: "console.heartbeat";
  scope?: ConsoleEventScope;
  time: string;
}

export type ConsoleLiveEvent = ConsoleChangedEvent | ConsoleHeartbeatEvent;

/** Which stream a subscription reads, and what it must be scoped to. */
export interface LiveScope {
  scope: ConsoleEventScope;
  /** Tenant id for the tenant plane; null for the owner-global plane. */
  scopeId: string | null;
}

export type DomainCounters = Readonly<Record<ConsoleDomain, number>>;

export function emptyCounters(): DomainCounters {
  return Object.fromEntries(CONSOLE_DOMAINS.map((domain) => [domain, 0])) as DomainCounters;
}

/** The contract this client can interpret; a newer server is handled conservatively below. */
export const SUPPORTED_CONTRACT_VERSION = 2;

export const RECONNECT_MIN_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/**
 * Exponential backoff between 1 and 30 seconds. `attempt` is 1 for the first retry after a drop;
 * the provider resets the count once a connection has proved itself, so a stream that keeps
 * working always reconnects fast while one that dies on arrival backs off.
 */
export function reconnectDelayMs(attempt: number): number {
  const exponent = Math.max(0, Math.floor(attempt) - 1);
  const delay = RECONNECT_MIN_MS * 2 ** Math.min(exponent, 20);
  return Math.min(RECONNECT_MAX_MS, delay);
}

/** A change event carries data for exactly one scope; anything else is not ours to apply. */
export function eventMatchesScope(event: ConsoleChangedEvent, expected: LiveScope): boolean {
  if (event.scope !== expected.scope) return false;
  if (event.scope === "owner_global") return true;
  // An event that does not name its tenant cannot be attributed to one, so it is not ours.
  if (!event.scope_id) return false;
  return event.scope_id === expected.scopeId;
}

export interface AppliedEvent {
  counters: DomainCounters;
  /** False when the event belonged to another scope and was dropped. */
  accepted: boolean;
}

/**
 * Bump the counters of the domains the event names. Anything this client cannot read precisely —
 * no domain list, a domain it does not know, a newer contract — moves every domain instead: a
 * client that cannot interpret the change must refetch too much, never too little.
 */
export function applyChangedEvent(
  counters: DomainCounters,
  event: ConsoleChangedEvent,
  expected: LiveScope,
): AppliedEvent {
  if (!eventMatchesScope(event, expected)) return { counters, accepted: false };
  const named = event.domains ?? [];
  const known = named.filter((domain): domain is ConsoleDomain =>
    (CONSOLE_DOMAINS as readonly string[]).includes(domain),
  );
  const readable =
    named.length > 0 &&
    known.length === named.length &&
    (event.contract_version ?? SUPPORTED_CONTRACT_VERSION) <= SUPPORTED_CONTRACT_VERSION;
  const moved = readable ? known : [...CONSOLE_DOMAINS];
  const next = { ...counters };
  for (const domain of moved) next[domain] = counters[domain] + 1;
  return { counters: next, accepted: true };
}

/** Sum of the counters a screen depends on; any change means "refetch". */
export function domainTick(counters: DomainCounters, domains: readonly ConsoleDomain[]): number {
  let total = 0;
  for (const domain of domains) total += counters[domain] ?? 0;
  return total;
}

/**
 * Identity of the data a screen is allowed to keep. It changes when the session or the browsed
 * business changes, which is what makes a screen drop the previous scope's rows instead of
 * rendering them under the new one.
 */
export function liveScopeKey(userId: string | null, tenantId: string | null): string {
  return `${userId ?? "anonymous"}:${tenantId ?? "none"}`;
}
